/**
 * Google Identity Services integration. Owns the Google SDK lifecycle
 * (load, initialize, render button, request fresh tokens) and reports
 * sign-in results back to the shared authState facade.
 */

import {
  authState,
  setSignedIn,
  setSessionToken,
  setRosterToken,
  getApiUrl,
  AuthIdentity,
} from "./authState";
import { getClientMetadata, withClientMetadata } from "../clientMetadata";

const GOOGLE_CLIENT_ID =
  "1051136762045-f4r30s2j65346s7hefl7lsd9ua1sc54f.apps.googleusercontent.com";

interface GoogleCredentialResponse {
  credential: string;
  select_by: string;
}

interface GoogleIdConfig {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
}

interface GoogleButtonConfig {
  theme?: "outline" | "filled_blue" | "filled_black";
  size?: "small" | "medium" | "large";
  type?: "standard" | "icon";
  shape?: "rectangular" | "pill" | "circle" | "square";
  text?: "signin_with" | "signup_with" | "continue_with" | "signin";
  width?: number;
}

interface GoogleAccountsId {
  initialize(config: GoogleIdConfig): void;
  renderButton(parent: HTMLElement, config: GoogleButtonConfig): void;
  prompt(): void;
  disableAutoSelect(): void;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: GoogleAccountsId;
      };
    };
  }
}

class GoogleAuthClient {
  private _idToken: string | undefined;
  private _signInWaiters: Array<(idToken: string) => void> = [];
  private _signInRejecters: Array<(err: Error) => void> = [];
  private _initialized = false;
  private _readyPromise: Promise<void> | undefined;

  public init(): void {
    if (this._initialized) return;
    this._initialized = true;
    this._readyPromise = this._waitForGisAndInitialize();
  }

  /** Render Google's official button into a DOM element. */
  public async renderSignInButton(
    parent: HTMLElement,
    options?: { width?: number },
  ): Promise<void> {
    await this._readyPromise;
    if (!window.google?.accounts?.id) {
      console.error("Google Identity Services failed to load");
      return;
    }
    window.google.accounts.id.renderButton(parent, {
      theme: "filled_blue",
      size: "large",
      type: "standard",
      shape: "rectangular",
      text: "continue_with",
      width: options?.width,
    });
  }

  /** Get a fresh ID token, prompting Google One Tap if needed. */
  public async requestFreshToken(): Promise<string> {
    if (this._idToken) return this._idToken;
    await this._readyPromise;

    return new Promise<string>((resolve, reject) => {
      this._signInWaiters.push(resolve);
      this._signInRejecters.push(reject);
      if (!window.google?.accounts?.id) {
        reject(new Error("Google Identity Services not loaded"));
        return;
      }
      window.google.accounts.id.prompt();
    });
  }

  public clearCachedToken(): void {
    this._idToken = undefined;
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
    }
  }

  /* ---------------------- private helpers ---------------------- */

  private _waitForGisAndInitialize(): Promise<void> {
    return new Promise<void>((resolve) => {
      const tryInit = () => {
        if (!window.google?.accounts?.id) {
          setTimeout(tryInit, 50);
          return;
        }
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response: GoogleCredentialResponse) => {
            this._handleGoogleCredential(response).catch((err) => {
              console.error("Sign-in handling failed:", err);
              this._failPendingWaiters(err instanceof Error ? err : new Error(String(err)));
            });
          },
          auto_select: false,
          cancel_on_tap_outside: false,
        });
        resolve();
      };
      tryInit();
    });
  }

  private async _handleGoogleCredential(response: GoogleCredentialResponse): Promise<void> {
    const idToken = response.credential;
    this._idToken = idToken;
    const client = await getClientMetadata();

    const meResponse = await fetch(`${getApiUrl()}/me`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(withClientMetadata({ idToken, provider: "google" }, client)),
    });

    if (!meResponse.ok) {
      const text = await meResponse.text();
      this._idToken = undefined;
      throw new Error(`/api/me failed: HTTP ${meResponse.status} ${text}`);
    }

    const data = (await meResponse.json()) as {
      playerId: string;
      provider: "google";
      email?: string;
      name?: string;
      sessionToken?: string;
      rosterToken?: string;
    };

    if (data.sessionToken) {
      setSessionToken(data.playerId, data.sessionToken);
    }
    if (data.rosterToken) {
      setRosterToken(data.playerId, data.rosterToken);
    }

    const identity: AuthIdentity = {
      playerId: data.playerId,
      provider: "google",
      email: data.email,
      name: data.name,
    };

    setSignedIn(identity, idToken);

    const waiters = this._signInWaiters.splice(0);
    this._signInRejecters.length = 0;
    for (const resolve of waiters) resolve(idToken);
  }

  private _failPendingWaiters(err: Error): void {
    const rejecters = this._signInRejecters.splice(0);
    this._signInWaiters.length = 0;
    for (const reject of rejecters) reject(err);
  }
}

export const googleAuth = new GoogleAuthClient();

// Backwards-compat re-export so the existing import sites keep working.
export { authState as state } from "./authState";
